import React, { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { matchPath, useNavigate } from 'react-router';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Config from '@skin_config';
import { configTypeMap } from '@app/dev/terminal/components/eventTypeFilter/constants';
import { pathMap } from '@app/dev/terminal/components/eventTypeFilter/helper';
import { useTranslation } from '@common/hooks/translation';
import SearchInput from '@common/components/searchInput';

import './style.scss';

const separatePageSportAliases = ['all', 'boosted', 'multiples'];
const minLettersCountForSearch = 3;

const EventTypeFilter = ({
  listType,
  gameType,
  sportAlias,
  searchText = '',
  setSearchText = () => { },
  configType,
}) => {
  const t = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const config = Config.routes.find((item) => {
    const nestedUrls = item.nestedItems?.flatMap((subItem) => (
      [subItem.url, ...(subItem.children?.map((child) => child.url) || [])]
    )).filter(Boolean) || [];

    const isActive = matchPath({ path: (item.url || ''), end: false }, pathname)
      || nestedUrls?.some((url) => matchPath({ path: url, end: false }, pathname))
      || Config[item.subItemsKey]?.some((subItem) => subItem.url === pathname);

    return isActive;
  });

  const enabledNestedItems = useMemo(
    () => config?.nestedItems?.filter((item) => item.enabled) || [],
    [config?.nestedItems],
  );

  const expandedItem = useMemo(() => {
    const active = enabledNestedItems.filter((item) => item?.children?.length).find((item) => (
      (item.url && pathname.startsWith(`/${item.url}`))
      || item.children.some((sub) => pathname.startsWith(`/${sub.url}`))
    ));

    if (active?.children?.length) {
      return active;
    }
    return null;
  }, [enabledNestedItems, pathname]);

  const searchActive = searchText.length >= minLettersCountForSearch;
  const isSeparate = sportAlias && separatePageSportAliases.includes(sportAlias);

  const navigateByType = (type) => {
    setSearchText('');
    const buildUrl = pathMap[type];
    if (!buildUrl) return;

    const url = buildUrl(sportAlias, gameType, isSeparate);
    navigate(url);
  };

  const handleNestedNavigation = (item) => {
    navigate(`/${item.url}`) || navigateByType(item.url || item.type);
  };

  const scrollLeft = () => {
    navigate(`/${Config.routes[0].url}`) || navigateByType(Config.routes[0].url || Config.routes[0].type);
  };

  const handleWheel = (e) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft += e.deltaY
    }
  };

  if (!enabledNestedItems.length) return null;

  return (
    <div className="event-type-filter">
      {expandedItem && (
        <i className="nav-icon global-icon icon-slim-arrow-left" onClick={scrollLeft} />
      )}
      <div className="pages-c-wrapper">
        <div
          className="pages-container"
          ref={containerRef}
          onWheel={handleWheel}
        >
          {expandedItem && (
            <>
              <div className="hasChildren">
                <i
                  className={
                    `event-icon global-icon icon-${expandedItem.icon}`
                    || expandedItem.icon
                  }
                />
                {t(expandedItem.name)}
              </div>
              {expandedItem.children.map((item) => (
                <div
                  key={item.name}
                  className={`sub-child ${pathname.startsWith(`/${item.url}`)
                    ? 'active'
                    : ''
                    }`}
                  onClick={() => handleNestedNavigation(item)}
                >
                  {t(item.name)}
                </div>
              ))}
            </>
          )}
          {config.enabled
            && !expandedItem
            && enabledNestedItems.map((item) => (
              <div
                key={item.url || item.type}
                onClick={() => handleNestedNavigation(item)}
                className={classnames('header-group', {
                  active:
                    pathname.startsWith(`/${item.url}`)
                    && !searchActive
                    && !item.children?.length,
                })}
              >
                <div
                  className="event-content"
                  aria-hidden="true"
                >
                  <i
                    className={
                      `event-icon global-icon ${item.icon === 'bet-builder' ? t(`icon-${item.icon}`) : `icon-${item.icon}`}`
                      || item.icon
                    }
                  />
                  {t(item.name)}
                  {item.hasNewLabel && (
                    <span className="new-label">
                      {t('New')}
                    </span>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {sportAlias !== 'multiples'
        && listType !== 'virtuals'
        && listType !== 'results'
        && listType !== 'popular'
        && configType === configTypeMap.sportsBook && (
          <SearchInput value={searchText} onChange={setSearchText} />
      )}
    </div>
  );
};

EventTypeFilter.propTypes = {
  listType: PropTypes.string,
  configType: PropTypes.string.isRequired,
  gameType: PropTypes.string,
  sportAlias: PropTypes.string,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
};

export default EventTypeFilter;
